import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, Select, MenuItem, TextField, Checkbox, ListItemText, InputAdornment, IconButton } from '@mui/material';
import Swal from 'sweetalert2';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';

const SCENARIO_TYPES = [
  { id: 1, scenario_type: "Positive" },
  { id: 2, scenario_type: "Negative" },
  { id: 3, scenario_type: "Variations" }
];

const FILE_TYPES = [
  { id: 0, file_type: "", label: "" },
  { id: 1, file_type: "OFAC_SDN", label: "OFAC SDN" },
  { id: 2, file_type: "OFAC_NON_SDN", label: "OFAC NON-SDN" },
  { id: 3, file_type: "UK_Sanctions", label: "UK Sanctions" },
  { id: 4, file_type: "PEP", label: "PEP" },
  { id: 5, file_type: "WATCH_LIST", label: "Watch List" },
  { id: 6, file_type: "NC", label: "Negative Case" },
  { id: 6, file_type: "PC-LFM", label: "PC-LFM" },
  { id: 6, file_type: "WHITE_LIST", label: "White List" },
];

const AddScenarioModal = ({ 
  modalOpen, 
  handleCloseModal, 
  modalType, 
  handleModalTypeChange, 
  modalFile, 
  setModalFile, 
  scenarioList, 
  modalScenario, 
  setModalScenario, 
  handleSaveCombination, 
  historyFiles, 
  selectedScenarioType, 
  handleFileChange, 
  handleScenarioTypeChange, 
  selectedFileType, 
  setSelectedFileType 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [scenarioDropdownOpen, setScenarioDropdownOpen] = useState(false);

  // Filter scenario types based on selected file type
  const getAvailableScenarioTypes = () => {
    if (!selectedFileType) return [];
    const type = selectedFileType.file_type;
    if (["OFAC_SDN", "OFAC_NON_SDN", "UK_Sanctions", "PEP", "WATCH_LIST"].includes(type)) {
      // Only Positive and Variations
      return SCENARIO_TYPES.filter(t => t.scenario_type === "Positive" || t.scenario_type === "Variations");
    } else if (["NC", "PC-LFM", "WHITE_LIST"].includes(type)) {
      // Only Negative
      return SCENARIO_TYPES.filter(t => t.scenario_type === "Negative");
    }
    return SCENARIO_TYPES;
  };

  // Helper to get filtered scenarios based on search
  const getFilteredScenarios = () => {
    if (!scenarioList) return [];
    return scenarioList.filter(scenario =>
      scenario.scenario_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(scenario.scenario_code).toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Helper to get scenario name by code
  const getScenarioNameByCode = (code) => {
    if (!scenarioList) return code;
    const scenario = scenarioList.find(s => String(s.scenario_code) === String(code));
    return scenario ? scenario.scenario_name : code;
  };

  // Helper to get selected scenario names for display
  const getSelectedScenarioNames = () => {
    if (!modalScenario || !Array.isArray(modalScenario)) return "";
    
    const validCodes = modalScenario.filter(code => 
      code !== null && code !== undefined && code !== '' && code !== 'select-all'
    );
    
    if (validCodes.length === 0) return "";
    
    const scenarioNames = validCodes.map(code => getScenarioNameByCode(code));
    return scenarioNames.join(", ");
  };

  // Helper to check if all filtered scenarios are selected
  const areAllSelected = () => {
    const filteredScenarios = getFilteredScenarios();
    if (!filteredScenarios || filteredScenarios.length === 0) return false;
    if (!modalScenario || modalScenario.length === 0) return false;
    
    // Convert all filtered scenario codes to strings for consistent comparison
    const validCodes = filteredScenarios.map(s => String(s.scenario_code));
    // Convert modalScenario to strings and filter out empty/null values
    const selectedCodes = modalScenario
      .filter(code => code !== null && code !== undefined && code !== '')
      .map(code => String(code));
    
    // Check if all filtered scenarios are in the selected codes
    return validCodes.length > 0 && validCodes.every(code => selectedCodes.includes(code));
  };

  // Helper to check if some but not all scenarios are selected
  const isIndeterminate = () => {
    // Remove indeterminate state - only show checked or unchecked
    return false;
  };

  const handleSelectAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const filteredScenarios = getFilteredScenarios();
    if (!filteredScenarios || filteredScenarios.length === 0) return;

    // Convert all filtered scenario codes to strings for consistency
    const validCodes = filteredScenarios.map(s => String(s.scenario_code));
    const currentSelected = (modalScenario || [])
      .filter(code => code !== null && code !== undefined && code !== '')
      .map(code => String(code));

    // If all filtered are selected, deselect all filtered
    if (validCodes.every(code => currentSelected.includes(code))) {
      // Remove only filtered scenarios from selection, keep others
      const remainingSelected = currentSelected.filter(code => !validCodes.includes(code));
      setModalScenario(remainingSelected);
    } else {
      // Add all filtered scenarios to selection (avoid duplicates)
      const newSelection = [...new Set([...currentSelected, ...validCodes])];
      setModalScenario(newSelection);
    }
  };

  const handleScenarioChange = (e) => {
    if (selectedScenarioType?.scenario_type === "Positive") {
      setModalScenario([e.target.value]);
    } else {
      let value = e.target.value;

      // If select-all is present, handle it separately
      if (Array.isArray(value) && value.includes("select-all")) {
        handleSelectAll(e);
        return;
      }

      setModalScenario(value);
      
      // Clear search query after selection
      setSearchQuery('');
    }

    // Keep dropdown open for multiple selection
    if (selectedScenarioType?.scenario_type !== "Positive") {
      setScenarioDropdownOpen(true);
    }
  };

  const handleCloseDropdown = () => {
    setScenarioDropdownOpen(false);
    setSearchQuery(''); // Clear search when dropdown closes
  };

  const handleCloseButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleCloseDropdown();
  };

  const handleSearchChange = (e) => {
    e.stopPropagation();
    setSearchQuery(e.target.value);
  };

  const handleSearchClick = (e) => {
    e.stopPropagation();
  };

  const handleSave = () => {
    if (!modalFile[0]) {
      Swal.fire({
        text: "Please select a file",
        icon: "warning",
        showConfirmButton: false,
        // timer: 3000
      });
      return;
    }

    if (!selectedFileType) {
      Swal.fire({
        text: "Please select a file type",
        icon: "warning",
        showConfirmButton: false,
        timer: 3000
      });
      return;
    }

    if (!selectedScenarioType) {
      Swal.fire({
        text: "Please select a scenario type",
        icon: "warning",
        showConfirmButton: false,
        timer: 3000
      });
      return;
    }

    if (selectedScenarioType.scenario_type === "Variations" && modalScenario.length === 0) {
      Swal.fire({
        text: "Please select at least one scenario",
        icon: "warning",
        showConfirmButton: false,
        timer: 3000
      }); 
      return;
    } 

    // Prepare scenario value for table row
    let scenarioValue;
    if (selectedScenarioType.scenario_type === "Positive") {
      scenarioValue = ["Positive"];
    } else if (selectedScenarioType.scenario_type === "Negative") {
      scenarioValue = ["Negative"];
    } else {
      scenarioValue = modalScenario;
    }

    handleSaveCombination({
      modalType,
      modalFile,
      scenario: scenarioValue,
      selectedFileType,
      selectedScenarioType
    });
  };

  const filteredScenarios = getFilteredScenarios();

  return (
    <Dialog open={modalOpen} onClose={(event, reason) => {
      if (reason !== 'backdropClick') {
        handleCloseModal();
      }
    }} maxWidth="xs" fullWidth>
      <div className='d-flex justify-content-between align-items-center'>
      <DialogTitle sx={{ fontWeight: 600 }}>Add New Scenario</DialogTitle>
      <IconButton onClick={handleCloseModal} color="black" sx={{ fontWeight: 600 }}>
          <CloseIcon />
        </IconButton>
      </div>
      
      <DialogContent>
        
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>File Type</div>
          <FormControl fullWidth size="small">
            <Select
              value={selectedFileType?.file_type || ""}
              onChange={e => {
                const selected = FILE_TYPES.find(type => type.file_type === e.target.value);
                setSelectedFileType(selected);
              }}
            >
              {FILE_TYPES.map((type) => (
                <MenuItem key={type.id} value={type.file_type}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Current/History</div>
          <FormControl fullWidth size="small">
            <Select value={modalType} onChange={handleModalTypeChange}>
              <MenuItem value="Current">Current</MenuItem>
              <MenuItem value="History">History</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>File Name</div>
          {modalType === "Current" ? (
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={modalFile[0] || "No File Found"}
              InputProps={{
                readOnly: true,
                disabled: true
              }}
            />
          ) : (
            <FormControl fullWidth size="small">
              <Select
                value={modalFile[0] || ""}
                onChange={(e) => handleFileChange(e.target.value)}
                renderValue={(selected) => selected}
              >
                {historyFiles?.length <= 0 && (
                  <MenuItem key={'not-found'} value={['']}>
                    File Not Found
                  </MenuItem>
                )}
                {historyFiles?.length > 0 && historyFiles.map((file) => (
                  <MenuItem key={file.file_name} value={file.file_name}>
                    {`${file.file_name} (${file.load_date} ${file.load_time})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Scenario Type</div>
          <FormControl fullWidth size="small" disabled={!modalFile[0]}>
            <Select
              value={selectedScenarioType?.scenario_type || ""}
              onChange={e => {
                const selected = SCENARIO_TYPES.find(type => type.scenario_type === e.target.value);
                handleScenarioTypeChange(selected);
              }}
            >
              {getAvailableScenarioTypes().map((type) => (
                <MenuItem key={type.id} value={type.scenario_type}>
                  {type.scenario_type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Only show Scenario field for Variations scenario type */}
        {selectedScenarioType && selectedScenarioType.scenario_type === "Variations" && (
          <div className="mb-2">
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Scenario</div>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={modalScenario}
                onChange={handleScenarioChange}
                onOpen={() => setScenarioDropdownOpen(true)}
                // onClose={handleCloseDropdown}
                open={scenarioDropdownOpen}
                renderValue={selected => {
                  if (Array.isArray(selected)) {
                    return getSelectedScenarioNames();
                  }
                  return selected || "";
                }}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 300 }
                  }
                }}
              >
                {/* X icon to close dropdown as a sticky MenuItem */}
                {/* <MenuItem key="close-dropdown" style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 3, display: 'flex', justifyContent: 'flex-end', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                  <IconButton
                    size="small"
                    onClick={
                      
                      (event, reason) => {
                        if (reason !== 'backdropClick') {
                          handleCloseButtonClick(event);
                        }
                      }
                    }
                    sx={{ color: 'rgba(0, 0, 0, 0.54)', '&:hover': { color: 'rgba(0, 0, 0, 0.87)' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </MenuItem> */}
                <MenuItem
                  key="close-dropdown"
                  style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 3,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(event, reason) => {
                      if (reason !== 'backdropClick') {
                        handleCloseButtonClick(event);
                      }
                    }}
                    sx={{
                      color: 'rgba(0, 0, 0, 0.54)',
                      '&:hover': { color: 'rgba(0, 0, 0, 0.87)' }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </MenuItem>

                {/* Search bar as sticky div */}
                <div style={{ position: 'sticky', top: 40, backgroundColor: 'white', zIndex: 2, padding: '8px 16px', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Search scenarios..."
                    autoComplete='off'
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onClick={handleSearchClick}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: '#888' }} />
                        </InputAdornment>
                      ),
                      style: { background: 'white', borderRadius: 6, fontSize: 16 }
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { paddingRight: 0 } }}
                  />
                </div>
                {/* Select All functionality for Variations - only show when no search query */}
                {!searchQuery && (
                  <MenuItem 
                    key="select-all" 
                    value="select-all" 
                    onClick={handleSelectAll}
                    style={{ 
                      position: 'sticky', 
                      top: 90, 
                      backgroundColor: 'white', 
                      zIndex: 10, 
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      pointerEvents: 'auto'
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <Checkbox checked={areAllSelected()} indeterminate={isIndeterminate()} />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                )}
                {filteredScenarios.map((scenario) => (
                  <MenuItem 
                    key={scenario.scenario_code} 
                    value={String(scenario.scenario_code)}
                    onClick={(e) => {
                      // Prevent the default Select behavior and handle manually
                      e.preventDefault();
                      e.stopPropagation();
                      
                      const scenarioCode = String(scenario.scenario_code);
                      const currentSelected = (modalScenario || [])
                        .filter(code => code !== null && code !== undefined && code !== '')
                        .map(code => String(code));
                      
                      let newSelection;
                      if (currentSelected.includes(scenarioCode)) {
                        // Remove if already selected
                        newSelection = currentSelected.filter(code => code !== scenarioCode);
                      } else {
                        // Add if not selected
                        newSelection = [...currentSelected, scenarioCode];
                      }
                      
                      setModalScenario(newSelection);
                      setSearchQuery(''); // Clear search after selection
                    }}
                  >
                    <Checkbox checked={modalScenario.includes(String(scenario.scenario_code))} />
                    <ListItemText primary={scenario.scenario_name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseModal} color="secondary" sx={{ fontWeight: 600 }}>
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" variant='contained' sx={{ fontWeight: 600, color: "white" }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddScenarioModal;